import { api, withFreshAccess } from './client'

export interface SearchPostImage {
  /** Full-size CDN URL. */
  fullsize: string
  /** Smaller thumbnail URL — useful if we want to economise on tokens. */
  thumb?: string
  alt?: string
}

export interface SearchPostHit {
  uri: string
  cid: string
  authorHandle: string
  authorDid: string
  text: string
  indexedAt: string
  images: SearchPostImage[]
}

interface RawSearchResp {
  cursor?: string
  posts?: Array<{
    uri: string
    cid: string
    author: { handle: string; did: string }
    record?: { text?: string }
    indexedAt: string
    embed?: {
      $type?: string
      images?: Array<{ fullsize: string; thumb?: string; alt?: string }>
      media?: {
        $type?: string
        images?: Array<{ fullsize: string; thumb?: string; alt?: string }>
      }
    }
  }>
}

interface Args {
  accountId: string
  q: string
  sort?: 'latest' | 'top'
  limit?: number
  /** Pagination cursor returned from a previous call — pass to get the next page. */
  cursor?: string
}

export interface SearchPostsResult {
  hits: SearchPostHit[]
  /** Pass back on the next call to get the next page. Undefined when exhausted. */
  cursor?: string
}

/** Wrap app.bsky.feed.searchPosts, returning ALL posts (including text-only).
 *  Used by text-mode AI campaigns. */
export async function searchPosts({
  accountId,
  q,
  sort = 'latest',
  limit = 25,
  cursor,
}: Args): Promise<SearchPostsResult> {
  let path =
    'app.bsky.feed.searchPosts?q=' +
    encodeURIComponent(q) +
    `&sort=${sort}&limit=${limit}`
  if (cursor) path += `&cursor=${encodeURIComponent(cursor)}`

  const r = await withFreshAccess(accountId, () =>
    api.xrpc<RawSearchResp>({
      accountId,
      method: 'GET',
      path,
    }),
  )
  const hits: SearchPostHit[] = []
  for (const p of r.posts ?? []) {
    const imgs = p.embed?.images ?? p.embed?.media?.images ?? []
    hits.push({
      uri: p.uri,
      cid: p.cid,
      authorHandle: p.author.handle,
      authorDid: p.author.did,
      text: p.record?.text ?? '',
      indexedAt: p.indexedAt,
      images: imgs.map((i) => ({ fullsize: i.fullsize, thumb: i.thumb, alt: i.alt })),
    })
  }
  return { hits, cursor: r.cursor }
}

/** Wrap app.bsky.feed.searchPosts with image extraction and cursor pagination.
 *  Used by AI campaigns and any future search-driven feature. */
export async function searchPostsWithImages({
  accountId,
  q,
  sort = 'latest',
  limit = 25,
  cursor,
}: Args): Promise<SearchPostsResult> {
  let path =
    'app.bsky.feed.searchPosts?q=' +
    encodeURIComponent(q) +
    `&sort=${sort}&limit=${limit}`
  if (cursor) path += `&cursor=${encodeURIComponent(cursor)}`

  const r = await withFreshAccess(accountId, () =>
    api.xrpc<RawSearchResp>({
      accountId,
      method: 'GET',
      path,
    }),
  )
  const hits: SearchPostHit[] = []
  for (const p of r.posts ?? []) {
    const imgs = p.embed?.images ?? p.embed?.media?.images ?? []
    if (imgs.length === 0) continue
    hits.push({
      uri: p.uri,
      cid: p.cid,
      authorHandle: p.author.handle,
      authorDid: p.author.did,
      text: p.record?.text ?? '',
      indexedAt: p.indexedAt,
      images: imgs.map((i) => ({
        fullsize: i.fullsize,
        thumb: i.thumb,
        alt: i.alt,
      })),
    })
  }
  return { hits, cursor: r.cursor }
}
